import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { usePreviewCarouselLayout } from '@bingads-webui-campaign-react/ad-placement-factory';
import { AdPlacementsGroupType } from '@bingads-webui-campaign-react/ad-placement-consts';
import { PerfMaxAdPreviewControls } from './ad-preview-controls';
import previewPlaceholder from '../media/preview-placeholder.png';
import { getAdPreviewStyles, getCarouselControlsAndIndicatorsStyleBySurface } from './style';
import { pmaxRequiredAspectRatios } from './shared';

const shortHeadlineMaxLength = 40;
const longHeadlineMaxLength = 90;
const descriptionMaxLength = 90;

export const PerfMaxAdPreview = (props) => {
  const {
    imageList,
    updateImageList,
    previewImagesSharedState,
    setPreviewImagesSharedState,
    isMSANAd,
    enableAutoPlay,
    placementCustomizationPredicate,
    adPlacementsGroupType = AdPlacementsGroupType.perfMax,
    hideHeaderButtons,
    isAppCampaignPreview,
  } = props;

  const adDataProps = ['shortHeadline', 'longHeadline', 'description', 'url', 'displayUrl', 'callToAction', 'businessName'];
  const currentAdData = _.pick(props, adDataProps);
  const [adData, setAdData] = React.useState(currentAdData);

  const isAdDataUpdated = useCallback(newAdData => (newAdData.length !== adData.length || !_.isMatch(newAdData, adData)), [adData]);
  const debouncedSetAdData = useMemo(() => _.debounce(setAdData, 500), []);
  if (isAdDataUpdated(currentAdData)) {
    debouncedSetAdData.cancel();
    debouncedSetAdData(currentAdData);
  }

  const getImageIndexFromId = useCallback((id) => {
    const index = _.findIndex(imageList, placement => _.first(placement).id === id && !_.first(placement).isLogo && !_.first(placement).isPortrait);
    return index >= 0 ? index : null;
  }, [imageList]);

  const getPreviewImageIndex = useCallback((idRequestedByImageExperience) => {
    let index = getImageIndexFromId(idRequestedByImageExperience);

    if (!index) {
      const firstImageIndex = _.findIndex(imageList, placement => !_.first(placement).isLogo && !_.first(placement).isPortrait);
      index = firstImageIndex >= 0 ? firstImageIndex : null;
    }

    return index;
  }, [getImageIndexFromId, imageList]);

  const { previewImageId } = previewImagesSharedState;

  let imagesToDisplay = [];
  let previewImageIndex = null;
  if (imageList && isAppCampaignPreview) {
    // for app campaign, always send all images for preview
    imagesToDisplay = imageList.flat();
  } else if (imageList && imageList.length > 0) {
    previewImageIndex = getPreviewImageIndex(previewImageId);
    imagesToDisplay = _.isNumber(previewImageIndex) ? imageList[previewImageIndex] : [];
  }

  const canExcludeAspectRatio = useCallback((typeToCheck) => {
    const minCount = pmaxRequiredAspectRatios[typeToCheck];
    if (!minCount) {
      return true;
    }

    let typeCount = 0;
    _.each(imageList, imageArray =>
      _.each(imageArray, (imageObj) => {
        if (imageObj.isIncluded && typeToCheck === imageObj.type) {
          typeCount += 1;
        }
      }));

    return (typeCount - 1) >= minCount;
  }, [imageList]);

  const getIsRequiredByIndex = useCallback((index, template) => {
    const { type } = template;
    return !canExcludeAspectRatio(type);
  }, [canExcludeAspectRatio]);

  const onAddAspectRatio = useCallback((type) => {
    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = true;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true });
    updateImageList(newImageList);
  }, [imageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState, updateImageList]);

  const onRemoveAspectRatio = useCallback((type) => {
    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = false;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true });
    updateImageList(newImageList);
  }, [imageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState, updateImageList]);

  const onEdit = useCallback((type) => {
    const cropImageObj = _.find(imageList[previewImageIndex], imagePlacement => imagePlacement.type === type);
    setPreviewImagesSharedState({ ...previewImagesSharedState, cropImageObj });
  }, [imageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState]);

  const truncatedAdData = useMemo(() => {
    const validAdData = { ...adData };

    if (validAdData.shortHeadline && validAdData.shortHeadline.length > shortHeadlineMaxLength) {
      validAdData.shortHeadline = validAdData.shortHeadline.substring(0, shortHeadlineMaxLength - 1);
    }

    if (validAdData.longHeadline && validAdData.longHeadline.length > longHeadlineMaxLength) {
      validAdData.longHeadline = validAdData.longHeadline.substring(0, longHeadlineMaxLength - 1);
    }

    if (validAdData.description && validAdData.description.length > descriptionMaxLength) {
      validAdData.description = validAdData.description.substring(0, descriptionMaxLength - 1);
    }

    return validAdData;
  }, [adData]);

  const [placementFilter, setPlacementFilter] = useState({ func: () => true });

  const getFirstElement = useCallback((item) => {
    if (_.isArray(item)) {
      return _.first(item);
    }
    return item;
  }, []);

  const getLogo = useCallback(() => {
    const logos = _.filter(imageList, imageArr =>
      getFirstElement(imageArr).isLogo === true);

    return _.first(logos);
  }, [getFirstElement, imageList]);

  const { PreviewCarouselLayout } = usePreviewCarouselLayout({
    groupType: adPlacementsGroupType,
    placementFilterPredicate: placementFilter.func,
    customDefaultImage: previewPlaceholder,
    placementCustomizationPredicate: placementCustomizationPredicate || null,
  });

  const classes = useDefaultStyles(getAdPreviewStyles);
  const [surfaceChoice, setSurfaceChoice] = useState({});
  const styleOverrides = useMemo(() => getCarouselControlsAndIndicatorsStyleBySurface(surfaceChoice), [surfaceChoice]);

  const onPlacementFilterChange = useCallback((newPlacementFilter) => {
    setPlacementFilter({ func: newPlacementFilter });
  }, []);

  return (
    <div className={classes.adPreviewContainer}>
      {!isMSANAd &&
        <PerfMaxAdPreviewControls onSurfaceChange={setSurfaceChoice} onPlacementFilterChange={onPlacementFilterChange} />}
      <PreviewCarouselLayout
        imagesToDisplay={imagesToDisplay}
        logo={_.find(getLogo(), logo => logo.isIncluded)}
        headerButtonProps={{
          onAddAspectRatio,
          onRemoveAspectRatio,
          onEdit,
        }}
        adData={truncatedAdData}
        getIsRequiredByIndex={getIsRequiredByIndex}
        enableAutoPlay={_.isNull(enableAutoPlay) ? isMSANAd : enableAutoPlay}
        styleOverrides={styleOverrides}
        hideHeaderButtons={hideHeaderButtons}
      />
    </div>);
};

PerfMaxAdPreview.propTypes = {
  imageList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  updateImageList: PropTypes.func.isRequired,
  previewImagesSharedState: PropTypes.shape({
    previewImageId: PropTypes.shape({}),
  }).isRequired,
  setPreviewImagesSharedState: PropTypes.func.isRequired,
  isMSANAd: PropTypes.bool,
  enableAutoPlay: PropTypes.bool,
  placementCustomizationPredicate: PropTypes.func,
  adPlacementsGroupType: PropTypes.string,
  hideHeaderButtons: PropTypes.bool,
  isAppCampaignPreview: PropTypes.bool,
};

PerfMaxAdPreview.defaultProps = {
  imageList: null,
  isMSANAd: false,
  enableAutoPlay: null,
  placementCustomizationPredicate: null,
  adPlacementsGroupType: AdPlacementsGroupType.perfMax,
  hideHeaderButtons: false,
  isAppCampaignPreview: false,
};
